import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from "react-query";
import { useTranslation } from "react-i18next";
import { App } from "antd";
import _ from "lodash";

import * as api from "apis/taskEss";
import { useReferenceSelector } from "hooks/useSelector";
import { useParams } from "react-router-dom";
import useTaskFeature from "components/apps/Task/hooks/useTaskFeature";

export function useTasksEss(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["tasks/ess", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTasksEss(payload);
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useTasksPaginationEss = (payload = {}, options = {}) => {
  return useInfiniteQuery(
    ["tasks/pagination/ess", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTasksEss({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) => {},
      ...options,
    }
  );
};

export function useTaskEss(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["task/ess", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskEss(payload?.id);
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useTaskEmployeesSuggestionEss = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useInfiniteQuery(
    ["task/employees/suggestion/ess", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskEmployeesEss({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export const useTaskClientsSuggestionEss = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useInfiniteQuery(
    ["task/clients/suggestion/ess", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskClientsEss({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export const useTaskApproversSuggestionEss = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useInfiniteQuery(
    ["task/approvers/suggestion/ess", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskApproversEss({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export const useAddTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.addTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.editTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useUpdateStatusTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  return useMutation((payload) => api.editStatusTaskEss(payload), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["task/ess", { id: payload?.id }]);
      const prev = queryClient.getQueryData(["task/ess", { id: payload?.id }]);
      queryClient.setQueryData(["task/ess", { id: payload?.id }], (old) => {
        if (old) {
          old.hr_task_status = payload.hr_task_status;
          if (payload?.hr_task_status_id === 3) {
            old.is_approvable = old?.hr_task_approval_status?.id !== 2 ? 1 : 0;
            old.is_rejectable = old?.hr_task_approval_status?.id !== 3 ? 1 : 0;
          } else {
            old.is_approvable = 0;
            old.is_rejectable = 0;
          }
          return old;
        }
      });
      return { prev };
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(["task/ess", { id: payload?.id }], context.prev);
      message.error(t("error.failed_save_data_to_server"));
    },
    onSettled: (response, error, payload) => {
      queryClient.invalidateQueries(["task/ess", { id: payload?.id }]);
    },
  });
};

export const useEditStatusTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();

  return useMutation(
    (payload) =>
      api.editStatusTaskEss({
        id: payload?.id,
        hr_task_status_id: payload?.hr_task_status_id,
      }),
    {
      onMutate: async (payload) => {
        await queryClient.cancelQueries([
          "tasks/pagination/ess",
          payload.source.queryKey,
        ]);
        await queryClient.cancelQueries([
          "tasks/pagination/ess",
          payload.destination.queryKey,
        ]);

        const prevSource = queryClient.getQueryData([
          "tasks/pagination/ess",
          payload.source.queryKey,
        ]);
        const prevDestination = queryClient.getQueryData([
          "tasks/pagination/ess",
          payload.destination.queryKey,
        ]);

        queryClient.setQueryData(
          ["tasks/pagination/ess", payload.source.queryKey],
          (old) => {
            if (old) {
              const pages = _.cloneDeep(old?.pages);
              const newPages = pages.map((page) => {
                const findData = page.data.filter(
                  (row) => row?.id !== payload?.id
                );
                page.total -= 1;
                return { ...page, data: findData };
              });
              old.pages = newPages;
              return old;
            }
          }
        );

        queryClient.setQueryData(
          ["tasks/pagination/ess", payload.destination.queryKey],
          (old) => {
            if (old) {
              const pages = _.cloneDeep(old?.pages);
              const newPages = pages.map((page, index) => {
                if (index === 0) {
                  page.total += 1;
                  if (!_.isEmpty(page?.data)) {
                    page.data = [{ ...payload.source.data }, ...page.data];
                  } else {
                    page.data = [{ ...payload.source.data }];
                  }
                }
                return page;
              });
              old.pages = newPages;
              return old;
            }
          }
        );

        return { prevSource, prevDestination };
      },
      onSuccess: ({ data }) => {
        if (data?.success) {
          message.success(data?.message);
        } else {
          message.error(data?.message);
        }
      },
      onError: (error, payload, context) => {
        queryClient.setQueryData(
          ["tasks/pagination/ess", payload.source.queryKey],
          context.prevSource
        );
        queryClient.setQueryData(
          ["tasks/pagination/ess", payload.destination.queryKey],
          context.prevDestination
        );
        message.error(t("error.failed_save_data_to_server"));
      },
      onSettled: (data, error, payload) => {
        queryClient.invalidateQueries([
          "tasks/pagination/ess",
          payload.source.queryKey,
        ]);
        queryClient.invalidateQueries([
          "tasks/pagination/ess",
          payload.destination.queryKey,
        ]);
      },
    }
  );
};

export const useArchiveTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.archiveTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useUnarchiveTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.unarchiveTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useApproveTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const { taskApprovalStatuses } = useReferenceSelector();
  return useMutation((payload) => api.approveTaskEss(payload), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["task/ess", { id: payload }]);
      const prev = queryClient.getQueryData(["task/ess", { id: payload }]);
      queryClient.setQueryData(["task/ess", { id: payload }], (old) => {
        const approvalStatus = taskApprovalStatuses.find(
          (row) => row?.id === 2
        );
        if (old) {
          old.hr_task_approval_status = approvalStatus;
          old.is_approvable = 0;
          old.is_rejectable = 1;
          return old;
        }
      });
      return { prev };
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useRejectTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const { taskApprovalStatuses } = useReferenceSelector();
  return useMutation((payload) => api.rejectTaskEss(payload), {
    onMutate: async (payload) => {
      await queryClient.cancelQueries(["task/ess", { id: payload }]);
      const prev = queryClient.getQueryData(["task/ess", { id: payload }]);
      queryClient.setQueryData(["task/ess", { id: payload }], (old) => {
        const approvalStatus = taskApprovalStatuses.find(
          (row) => row?.id === 3
        );
        if (old) {
          old.hr_task_approval_status = approvalStatus;
          old.is_approvable = 1;
          old.is_rejectable = 0;
          return old;
        }
      });
      return { prev };
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useDeleteTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.deleteTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useDeleteAttachmentTaskEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.deleteAttachmentTaskEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditTaskRepeatSettingsEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const paramsURL = useParams();
  const { query } = useTaskFeature();
  return useMutation((payload) => api.editTaskRepeatSettingsEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
        if (data?.data?.id) {
          queryClient.setQueryData(
            [query.keyTask, { id: paramsURL?.id }],
            (old) => {
              if (old) {
                const prevData = _.cloneDeep(old);
                prevData.hr_task_repeat_setting = {
                  ...old?.hr_task_repeat_setting,
                  ...data?.data,
                };
                return prevData;
              }
            }
          );
        }
        queryClient.invalidateQueries([query.keyTask, { id: paramsURL?.id }]);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useDisabledTaskRepeatSettingsEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const paramsURL = useParams();
  const { query } = useTaskFeature();
  return useMutation((id) => api.disabledTaskRepeatSettingsEss(id), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
        queryClient.setQueryData(
          [query.keyTask, { id: paramsURL?.id }],
          (old) => {
            if (old) {
              const prevData = _.cloneDeep(old);
              prevData.hr_task_repeat_setting = {
                ...old?.hr_task_repeat_setting,
                is_active: 0,
              };
              return prevData;
            }
          }
        );
        queryClient.invalidateQueries([query.keyTask, { id: paramsURL?.id }]);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEnableTaskRepeatSettingsEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const paramsURL = useParams();
  const { query } = useTaskFeature();
  return useMutation((data) => api.enableTaskRepeatSettingsEss(data), {
    onSuccess: ({ data }, payload) => {
      if (data?.success) {
        message.success(data?.message);
        queryClient.setQueryData(
          [query.keyTask, { id: paramsURL?.id }],
          (old) => {
            if (old) {
              const prevData = _.cloneDeep(old);
              prevData.hr_task_repeat_setting = {
                ...old?.hr_task_repeat_setting,
                repeat_from: payload?.repeat_from,
                repeat_until: payload?.repeat_until,
                is_active: 1,
              };
              return prevData;
            }
          }
        );
        queryClient.invalidateQueries([query.keyTask, { id: paramsURL?.id }]);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};
